/**
 * Formatting helpers.
 *
 * Every amount in this app is rupees. Money crosses the API as integer minor
 * units — paise — so the division happens here and nowhere else: a component
 * that formats its own would eventually get the scale wrong.
 */

const RUPEE = '₹';

export function money(amountMinor, { compact = false } = {}) {
  if (amountMinor == null || Number.isNaN(Number(amountMinor))) return '—';

  const major = Number(amountMinor) / 100;
  const negative = major < 0;
  const value = Math.abs(major);
  const sign = negative ? '-' : '';

  // Compact rupees are read in crore and lakh, not M and k.
  if (compact) {
    if (value >= 10_000_000) return `${sign}${RUPEE}${(value / 10_000_000).toFixed(1)}Cr`;
    if (value >= 100_000) return `${sign}${RUPEE}${(value / 100_000).toFixed(1)}L`;
    if (value >= 1_000) return `${sign}${RUPEE}${(value / 1_000).toFixed(1)}k`;
  }

  const formatted = value.toLocaleString('en-IN', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return `${sign}${RUPEE}${formatted}`;
}

/**
 * Minor units → a plain rupee number for a form input, and back.
 *
 * A price field has to hold an editable number rather than a formatted string,
 * but the ×100 still belongs here — a form that did its own would be the
 * second place in the app that knows the scale.
 */
export function rupeesFromMinor(amountMinor) {
  if (amountMinor == null || Number.isNaN(Number(amountMinor))) return '';
  return String(Number(amountMinor) / 100);
}

export function minorFromRupees(rupees) {
  const value = Number(rupees);
  if (rupees === '' || rupees == null || Number.isNaN(value)) return null;
  return Math.round(value * 100);
}

export function number(value) {
  if (value == null) return '—';
  return Number(value).toLocaleString('en-IN');
}

export function percent(value, digits = 0) {
  if (value == null) return '—';
  return `${Number(value).toFixed(digits)}%`;
}

/**
 * A calendar date as dd/mm/yyyy.
 *
 * Zero-padded and fixed, rather than locale-driven: this is what goes on the
 * printed ID card, where 01/04/2025 must not read as 4 January to one person
 * and 1 April to another.
 *
 * Read in UTC on purpose. A joining date is a calendar day, and both the form
 * value ("2025-04-01") and the stored value parse to midnight UTC — reading
 * them with local getters would show 31/03/2025 anywhere west of Greenwich.
 */
export function dateNumeric(value) {
  if (!value) return '—';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  const day = String(parsed.getUTCDate()).padStart(2, '0');
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${parsed.getUTCFullYear()}`;
}

export function date(value, { withTime = false } = {}) {
  if (!value) return '—';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

export function relative(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  const diffMs = Date.now() - parsed.getTime();
  const minutes = Math.round(diffMs / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;

  return date(value);
}

/** "pending_verification" → "Pending verification". */
export function humanise(value) {
  if (!value) return '—';
  const spaced = String(value).replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * A phone number as it appears on the ID card, always +91-prefixed.
 *
 * A leading 91 is only treated as the country code when what follows is still
 * a full number — an Indian mobile can legitimately begin "91". Whatever
 * grouping was typed is kept, so "83199-59130" stays readable.
 */
export function phoneWithCountryCode(value) {
  if (!value) return '';

  let local = String(value).trim().replace(/^\+/, '');

  if (/^91[\s-]*/.test(local) && local.replace(/\D/g, '').length > 10) {
    local = local.replace(/^91[\s-]*/, '');
  } else if (/^0[\s-]*/.test(local)) {
    local = local.replace(/^0[\s-]*/, '');
  }

  return `+91-${local.trim()}`;
}

export function initials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

export function truncate(value, max = 60) {
  if (!value) return '';
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}
